import { Button } from "@mui/material";
import { useCallback, useState } from "react";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import adminApi from "../../../../../api/adminApi";
import type { ResourceChangeProgressNotification } from "../../../../../api/types/resourceChangesTypes";
import useClientContext from "../../../../../context/useClientContext";
import useResourceChanged from "../../../../../hooks/useResourceChanged";
import ImportDataStatusTag from "../../../import-data/ImportDataStatusTag";
import { getDataImportPagePath, isImportOperationRunning } from "../../../import-data/importDataStateHelper";
import ContentPanelLayout from "../ContentPanelLayout";
import DataImportPanelBody from "./DataImportPanelBody";

interface Props {
  contentId: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
}

const DataImportPanel = ({ contentId, isExpanded, onToggleExpand, onClose }: Props) => {
  const { clientCode } = useClientContext();
  const [liveProgress, setLiveProgress] = useState<ResourceChangeProgressNotification>();

  const getDetails = useCallback(() => adminApi.getDataImportDetails(contentId), [contentId]);
  const getMetadata = useCallback(() => adminApi.getDataImportMetadata(contentId), [contentId]);

  const [dataImport, detailsError, { fetch: fetchDetails, fetchSilent: fetchDetailsSilent }] = useFetch(getDetails);
  const [dataImportMetadata, metadataError] = useFetch(getMetadata);

  useResourceChanged("DataImport", contentId, {
    onChanged: async () => {
      const error = await fetchDetailsSilent();
      if (error) {
        logError(error, "[DataImportPanel] fetchDetailsSilent");
      }
      setLiveProgress(undefined);
    },
    onProgress: setLiveProgress,
    isOperationRunning: dataImport !== undefined && isImportOperationRunning(dataImport),
  });

  return (
    <ContentPanelLayout
      title={
        dataImport
          ? `${dataImport.description || dataImport.sourceDescription} • ${convertISODate(dataImport.createdAt)}`
          : "Data Import"
      }
      status={dataImport && <ImportDataStatusTag dataImportState={dataImport.state} />}
      actions={
        dataImport && (
          <Button
            variant="outlined"
            color="secondary"
            href={getDataImportPagePath(clientCode, dataImport)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Data Import
          </Button>
        )
      }
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
      onClose={onClose}
    >
      <DataImportPanelBody
        dataImport={dataImport}
        dataImportMetadata={dataImportMetadata}
        fetchError={detailsError || metadataError}
        progress={liveProgress}
        onRefresh={fetchDetails}
      />
    </ContentPanelLayout>
  );
};

export default DataImportPanel;
